import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TableCell from "@mui/material/TableCell"
import TextField from "@mui/material/TextField"
import { useMemo, useState } from "react"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminExportCsvButton from "../components/AdminExportCsvButton"
import {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminLiquidToolbar from "../components/AdminLiquidToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"
import MeetingDetailsDialog from "./MeetingDetailsDialog"
import MeetingStatusChip from "./MeetingStatusChip"
import MeetingsBookButton from "./MeetingsBookButton"
import {
  MEETING_STATUS_FILTER_OPTIONS,
  formatMeetingDateTime,
  meetingCandidateNames,
  providerLabel,
} from "./meetingUtils"

export default function MeetingsListPage() {
  const [status, setStatus] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [selectedMeetingId, setSelectedMeetingId] = useState(null)

  const extraParams = useMemo(
    () => ({
      status: status || undefined,
      start: startDate || undefined,
      end: endDate || undefined,
    }),
    [status, startDate, endDate]
  )

  const query = useAdminPaginatedQuery(adminApi.fetchMeetings, {
    extraParams,
    defaultSortColumn: "start_datetime",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    searchText,
    setSearchText,
    exportParams,
    refresh,
  } = query

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Meetings"
        subtitle="All meetings across users. Click a column heading to sort."
        actions={<MeetingsBookButton onBooked={() => refresh()} />}
      />
      <AdminLiquidToolbar>
        <TextField
          size="small"
          label="Search meetings"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}
        />
        <Select
          size="small"
          displayEmpty
          inputProps={{ "aria-label": "Status" }}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 170 }}
        >
          {MEETING_STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value || "all"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          type="date"
          label="From"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <AdminExportCsvButton
          exportFn={adminApi.exportMeetings}
          params={exportParams}
          filenamePrefix="meetings"
        />
      </AdminLiquidToolbar>
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={6}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="start_datetime"
              label="When"
              query={query}
              minWidth={{ xs: 96, sm: 128 }}
            />
            <AdminSortableHeaderCell
              columnName="candidate"
              label="Candidate"
              query={query}
              minWidth={{ xs: 88, sm: 112 }}
            />
            <AdminSortableHeaderCell
              columnName="user"
              label="User"
              query={query}
              minWidth={{ xs: 88, sm: 112 }}
            />
            <AdminSortableHeaderCell
              columnName="status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminSortableHeaderCell
              columnName="provider"
              label="Calendar"
              query={query}
              minWidth={{ xs: 64, sm: 80 }}
            />
            <AdminSortableHeaderCell
              columnName="source"
              label="Booked via"
              query={query}
              minWidth={{ xs: 56, sm: 72 }}
            />
          </AdminTableHeadRow>
        }
      >
        {rows.map((meeting) => (
          <AdminTableRow
            key={meeting.id}
            hover
            onClick={() => setSelectedMeetingId(meeting.id)}
            sx={{ cursor: "pointer" }}
          >
            <AdminTruncatedCell
              limit={40}
              value={formatMeetingDateTime(meeting.start_datetime)}
            />
            <AdminTruncatedCell
              limit={40}
              value={meetingCandidateNames(meeting)}
            />
            <AdminTruncatedCell
              limit={40}
              value={meeting.host?.name || meeting.host?.email || "—"}
            />
            <TableCell sx={adminTableBodyCellSx}>
              <MeetingStatusChip status={meeting.display_status} />
            </TableCell>
            <AdminTruncatedCell value={providerLabel(meeting.provider)} />
            <AdminTruncatedCell value={meeting.source} />
          </AdminTableRow>
        ))}
      </AdminListingTable>
      <MeetingDetailsDialog
        open={selectedMeetingId !== null}
        meetingId={selectedMeetingId}
        onClose={() => setSelectedMeetingId(null)}
        onChanged={() => refresh()}
      />
    </Column>
  )
}
